import { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ApiError, ago, api, atLeast, type Bench, type BenchAutoInfo } from "../api";
import { Notice, Section } from "./Look";

const pad = (n: number) => String(n).padStart(2, "0");

/** A local "HH:MM" as UTC. `offsetMin` is Date#getTimezoneOffset: minutes
 *  from local time to UTC (Helsinki in summer: -180). */
export function localToUtc(hhmm: string, offsetMin = new Date().getTimezoneOffset()): string {
  const [h, m] = hhmm.split(":").map(Number);
  const t = (((h * 60 + m + offsetMin) % 1440) + 1440) % 1440;
  return `${pad(Math.floor(t / 60))}:${pad(t % 60)}`;
}

/** A UTC "HH:MM" in the reader's local time. */
export function utcToLocal(hhmm: string, offsetMin = new Date().getTimezoneOffset()): string {
  return localToUtc(hhmm, -offsetMin);
}

/**
 * What a bench tests on its own: a campaign for every new build, one every
 * night (extension #1 after the profiles plan).
 *
 * The section says it in a sentence first - what the bench runs by itself, what
 * it is waiting for and why, the last campaign it started - and only a
 * maintainer gets the switches, because they decide when hardware moves with
 * nobody watching. A bench that cannot run campaigns has nothing to schedule
 * and shows nothing.
 */
export default function AutoTesting({ bench }: { bench: Bench }) {
  const { t } = useTranslation();
  const [info, setInfo] = useState<BenchAutoInfo | null>(null);
  const [onNew, setOnNew] = useState(false);
  const [nightly, setNightly] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const may = atLeast(bench.role, "maintainer");
  const canRun = (bench.agent_caps ?? []).includes("profiles");

  const load = useCallback(async () => {
    try {
      const d = await api.benchAuto(bench.id);
      setInfo(d);
      setOnNew(d.settings.on_new_build);
      setNightly(d.settings.nightly_utc ? utcToLocal(d.settings.nightly_utc) : "");
    } catch {
      /* a service from before automatic testing: nothing to show */
    }
  }, [bench.id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!info) return null;
  const s = info.settings;
  const st = info.state;
  if (!canRun && !s.on_new_build && !s.nightly_utc) return null;

  const parts = [
    s.on_new_build ? t("auto.onNewBuild") : null,
    s.nightly_utc ? t("auto.nightlyAt", { time: utcToLocal(s.nightly_utc) }) : null,
  ].filter(Boolean);
  const what = (origin: string) => t(`auto.origin.${origin}` as "auto.origin.nightly");
  const build = (b: string) => b || t("auto.installed");

  async function save() {
    setBusy(true);
    setError("");
    setSaved(false);
    try {
      await api.setBenchAuto(bench.id, onNew, nightly ? localToUtc(nightly) : null);
      setSaved(true);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section title={t("auto.title")}>
      <p className="text-sm text-slate-700 dark:text-slate-300" data-testid="auto-summary">
        {parts.length ? t("auto.summary", { what: parts.join(t("auto.and")) }) : t("auto.off")}
      </p>
      {/* The schedule above is a promise the bench keeps only while it is
          talking: the agent picks the campaign up from its own heartbeat. Said
          plainly, or the sentence above goes on claiming nightly testing for a
          bench that went quiet (displayos-headless, 17 Sep 2026, eleven nights). */}
      {parts.length > 0 && !(bench.agent_alive ?? bench.online) && bench.last_seen_at && (
        <p className="mt-1 text-sm font-medium text-amber-800 dark:text-amber-300" data-testid="auto-stalled">
          {t("auto.stalled", { when: ago(bench.last_seen_at) })}
        </p>
      )}
      {st.waiting && (
        <p className="mt-1 text-sm text-amber-800 dark:text-amber-300">
          {t("auto.waiting", { what: what(st.waiting.origin), build: build(st.waiting.build), why: st.waiting.why })}
        </p>
      )}
      {st.last_campaign && (
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
          <Trans
            i18nKey="auto.last"
            values={{ what: what(st.last_campaign.origin), build: build(st.last_campaign.build),
                      when: ago(st.last_campaign.at) }}
            components={{ campaignLink: <Link className="underline" to={`/campaigns/${st.last_campaign.id}`} /> }}
          />
        </p>
      )}
      {st.same_product && (
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300" data-testid="auto-same-product">
          {t("auto.sameProduct", { build: st.same_product.build, product: st.same_product.product,
                                   when: ago(st.same_product.at) })}
        </p>
      )}
      {st.missed && (
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
          {t("auto.missed", { day: st.missed.day, why: st.missed.why })}
        </p>
      )}

      {may && (
        <div className="mt-2 space-y-2 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={onNew} onChange={(e) => setOnNew(e.target.checked)} />
            {t("auto.newBuildLabel")}
          </label>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={nightly !== ""}
                onChange={(e) => setNightly(e.target.checked ? "02:00" : "")}
              />
              {t("auto.nightlyLabel")}
            </label>
            <input
              type="time"
              aria-label={t("auto.nightlyTime")}
              className="rounded border border-slate-300 px-2 py-1 dark:border-slate-700 dark:bg-slate-800"
              value={nightly}
              disabled={nightly === ""}
              onChange={(e) => setNightly(e.target.value)}
            />
            <span className="text-xs text-slate-500">{t("auto.localTime")}</span>
          </div>
          <p className="text-xs text-slate-500">{t("auto.cost")}</p>
          <div className="flex items-center gap-3">
            <button type="button" className="btn-primary" disabled={busy} onClick={() => void save()}>
              {t("auto.save")}
            </button>
            {saved && <span className="text-xs text-slate-600 dark:text-slate-300">{t("auto.saved")}</span>}
          </div>
          {error && <Notice tone="error">{error}</Notice>}
        </div>
      )}
    </Section>
  );
}
